<script>
    import {PATH_PREFIX} from '../../apiUrls.js';

    import {onMount} from 'svelte';
    import ComboSelect from '../ComboSelect/ComboSelect.svelte';
    import MegaSelect from '../MegaSelect/MegaSelect.svelte';
    import Messages from '../common/Messages.svelte';
    import Modal from '../Modal.svelte';
    import Button from '../Button.svelte';
    import yup from 'yup';
    import { get, submit } from '../../utils.js';
    import { sgettext } from '../../i18n.js';
    import { branchId, draftElementDetails, elementInstance, elementDetails, activityVersion, isMaterialRiskControlSelected, isSaveEdit } from '../Workspaces/workspacesStore.js';

    export let title = sgettext('$$SELECT_MATERIAL_RISK$$');
    export let id = 'materialRiskEditorModal';
    export let elementVersionId = null;

    let actionUrl = `${PATH_PREFIX}/sw/api/elements_database/elementversions/`;
    let optionsApi = `${PATH_PREFIX}/sw/api/applicable-material-risks-grouped-by-energy-type-and-mechanism/`;
    let draftElementVersionViewApiUrl = `${PATH_PREFIX}/sw/api/elements_database/draft_element_version/`;
    let options = [];
    let hazardOptions = [];
    let controlOptions = [];
    let loadingHazardOptions = false;
    let changes = false;
    let saving = false;
    let saveSuccess = false;
    let energyType = null;
    let mechanism = null;
    let globalHazard = {id: ''};
    let globalControl = {id: ''};
    let mechanismId = null;
    let globalHazardId = null;
    let globalControlId = null;
    let originalMechanism = null;
    let originalGlobalHazard = null;
    let originalGlobalControl = null;

    export let mechanismListPlacement = 'auto';
    export let mechanismListAbsoluteParentId = undefined;
    export let mechanismListAbsoluteScrollParentSelector = undefined;

    $: newElementState = elementVersionId === 0;

    $: changes = globalHazardId !== originalGlobalHazard || globalControlId !== originalGlobalControl;

    $: isValid = $draftElementDetails ? ($draftElementDetails.materialRisk ? ($draftElementDetails.materialRisk.valid ? true :false): true) : true;

    let previouslySelectedMaterialRiskIsNoLongerValid = false;

    $: draftElementDetails.set($draftElementDetails);

    $: if(!$isSaveEdit) {
        energyType = null;
        mechanism = null;
        mechanismId = null;
        globalHazardId = null;
        globalControlId = null;
    }

    async function getElementVersionDetails(){
        const elementId = $elementDetails ? $elementDetails.elementId : 0;
        if(elementId) {
            const resp = await get(`${draftElementVersionViewApiUrl}${elementId}/`);

            if(resp.ok){
                const json = await resp.json();
                let details = json;
                details.competencies = json.competencies.map(c => ({id: c.competency, name: c.name}));
                elementVersionId = details.evId;
                draftElementDetails.set(details);
            } else {

            }
        }
    }

    onMount(async () => {
        await getHazardOptions();

        if ($draftElementDetails === null) {
            await getElementVersionDetails();
        }

        if (newElementState) {
            hazardOptions = getAllHazardsFromEnergyTypes(options);

        } else {
            // These two variables are used to check if the material risk currently associated with the element
            // is still a valid option
            let globalHazardFromHazardOptions = null;
            let globalControlFromControlOptions = null;

            // is Safety Element
            if ($draftElementDetails?.type === 'SAFETY') {
                mechanism = {...$draftElementDetails.materialRisk.mechanism};

                if (mechanism.id) {
                    mechanismId = mechanism.id;
                    getEnergyTypeAndMechanismFromId();
                    hazardOptions = getAllHazardsFromEnergyTypes(options);
                }

                globalHazard = {...$draftElementDetails.materialRisk.hazard};
                globalControl = {...$draftElementDetails.materialRisk.control};
            }

            globalHazardId = globalHazard.id;

            if (globalHazard.id) {
                globalHazardFromHazardOptions = getHazardFromId();
                if (globalHazardFromHazardOptions) {
                    controlOptions = globalHazardFromHazardOptions.global_controls;
                }
            }

            globalControlId = globalControl.id;

            if (globalHazardFromHazardOptions) {
                try {
                    globalControlFromControlOptions = controlOptions ? controlOptions.filter(o => o.id === globalControlId)[0] : null;
                }
                catch {

                }
            }

            originalMechanism = mechanism;
            originalGlobalHazard = globalHazardId;
            originalGlobalControl = globalControlId;

            previouslySelectedMaterialRiskIsNoLongerValid = !globalHazardFromHazardOptions || !globalControlFromControlOptions;
        }
    });

    const schema = yup.object().shape({
        global_hazard: yup.number()
                .required(sgettext('$$REQUIRED_FIELD$$'))
                .typeError(sgettext('$$REQUIRED_FIELD$$')),
        global_control: yup.number()
                .required(sgettext('$$REQUIRED_FIELD$$'))
                .typeError(sgettext('$$REQUIRED_FIELD$$'))
    });

    let getAllHazardsFromEnergyTypes = energyTypes => {
        let result = [];
        for (let oEnergyType of energyTypes) {
            for (let oMechanism of oEnergyType.mechanism_options) {
                for (let oHazard of oMechanism.hazard_options) {
                    let aHazard = {...oHazard};
                    aHazard.energyType = {...oEnergyType};
                    aHazard.mechanism = {...oMechanism};
                    result.push({...aHazard});
                }
            }
        }
        return result;
    };

    async function getHazardOptions() {
        loadingHazardOptions = true;

        const response = await get(`${optionsApi}${$branchId}/`);

        if (response.ok) {
            const json = await response.json();
            options = json;
        }

        loadingHazardOptions = false;
    }

    function getEnergyTypeAndMechanismFromId() {
        mechanism = null;
        energyType = null;
        hazardOptions = [];

        if (!mechanismId) return;

        for (let energyTypeOption of options) {
            for (let mechanismOption of energyTypeOption.mechanism_options) {
                if (mechanismOption.id == mechanismId) {
                    mechanism = {...mechanismOption};
                    energyType = {...energyTypeOption};
                    hazardOptions = [...mechanism.hazard_options];
                    return;
                }
            }
        }
    }

    function getHazardFromId() {
        return hazardOptions.filter(o => o.id === globalHazardId)[0];
    }

    function getControlFromId() {
        return globalHazard.global_controls.filter(o => o.id === globalControlId)[0];
    }

    let isObjectIdChanged = (oldObject, newObject) => {
        return newObject?.id != oldObject?.id;
    };

    let doSelectHazard = newGlobalHazardId => {
        globalHazardId = newGlobalHazardId;
        globalHazard = getHazardFromId();

        if (globalHazard) {
            if (globalHazard.energyType) energyType = {...globalHazard.energyType};
            if (globalHazard.mechanism) mechanism = {...globalHazard.mechanism};

            controlOptions = [...globalHazard.global_controls];

            if (controlOptions.length == 1) {
                globalControlId = controlOptions[0].id;
                globalControl = getControlFromId();
            }
        }
    }

    let handleSelectMechanism = (event, item) => {
        if (isObjectIdChanged(mechanism, event.detail)) {
            mechanism = {...event.detail};
            globalHazardId = null;
            globalHazard = {id: null};
            hazardOptions = [...mechanism.hazard_options];
            globalControlId = null;
            globalControl = {id: null};
            controlOptions = [];

            if (hazardOptions.length == 1) {
                doSelectHazard(hazardOptions[0].id);
            }
        }
    };

    let handleClearMechanism = event => {
        energyType = null;
        mechanismId = null;
        mechanism = null;
        globalHazardId = null;
        globalHazard = {id: null};
        hazardOptions = getAllHazardsFromEnergyTypes(options);
        globalControlId = null;
        globalControl = {id: null};
        controlOptions = [];
    };

    let handleHazardChanged = () => {
        globalControl = {id: ''};
        globalControlId = null;

        if (globalHazardId) doSelectHazard(globalHazardId);
    }

    function openModal() {
        jQuery(`#${id}`).modal('show');
    }

    function revertChanges() {
        mechanism = originalMechanism;
        mechanismId = mechanism.id;
        getEnergyTypeAndMechanismFromId();

        globalHazardId = originalGlobalHazard;
        globalControlId = originalGlobalControl;

        try {
            globalHazard = getHazardFromId();
            controlOptions = globalHazard.global_controls;
        } catch {
            previouslySelectedMaterialRiskIsNoLongerValid = true;
        }
    }

    function finishSelection() {
        jQuery(`#${id}`).modal('hide');
        try {
            globalHazard = getHazardFromId();
            globalControl = getControlFromId();

            let updatedElementDetails = {...$draftElementDetails};
            updatedElementDetails.materialRisk = {
                hazard: globalHazard,
                control: globalControl,
            };
            draftElementDetails.set(updatedElementDetails);
            isMaterialRiskControlSelected.set(true);
            isSaveEdit.set(true)
        }
        catch (_errors) {

        }
    }

    async function saveChanges() {

        saving = true;

        globalHazard = getHazardFromId();
        globalControl = getControlFromId();

        const payload = {
            global_hazard: globalHazard.id,
            global_control: globalControl.id,
            activity_version_id: $activityVersion ? $activityVersion.id : 0
        };

        const resp = await submit(`${actionUrl}${$draftElementDetails.evId}/`, 'PATCH', payload);
        saving = false;

        if (resp.ok) {
            let updatedElementDetails = {...$draftElementDetails};
            updatedElementDetails.materialRisk = {
                hazard: globalHazard,
                control: globalControl,
            };
            draftElementDetails.set(updatedElementDetails);

            originalGlobalHazard = globalHazard.id;
            originalGlobalControl = globalControl.id;

            hazardOptions = getAllHazardsFromEnergyTypes(options);

            saveSuccess = true;
            setTimeout(() => {saveSuccess = false}, 1000);

            previouslySelectedMaterialRiskIsNoLongerValid = false;
        }
    }
</script>

<style>
    .material-risk-display {
        background-color: rgba(0,0,0,.05);
        padding: 10px;
        border-radius: 2px;
    }

    .grid {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        grid-column-gap: 10px;
    }

    .name {
        display: grid;
        grid-template-columns: 50px 1fr;
        grid-column-gap: 10px;
    }

    .details-row {
        padding: 20px;
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        grid-column-gap: 20px;
        background-color: rgba(0,0,0,0.05);
    }

    img {
        max-width: 54px;
        padding-right: 5px;
    }
</style>

{#if newElementState}

        <div class="material-risk-display">
            <p class="font-weight-bold">{sgettext('$$MATERIAL_RISK_ELEMENT_CONTROLLING_1$$')}:</p>
            {#if $isSaveEdit && globalHazard && globalHazard.id && globalHazard.icon && globalControl && globalControl.id && globalControl.icon}
                <div class="grid">
                    <div class="column name">
                        <div class="item-icon">
                            <img
                                src={energyType.icon.url}
                                alt={sgettext('$$ENERGY_ICON$$')} />
                        </div>
                        <div>
                            <p class="font-weight-bold">{sgettext('$$ENERGY$$')}: {energyType.name}</p>
                            <p>{sgettext('$$MECHANISM$$')}: {mechanism.name}</p>
                        </div>
                    </div>
                    <div class="column name">
                        <div class="item-icon">
                            <img
                                src={globalHazard.icon.url}
                                alt={sgettext('$$RISK_ICON$$')} />
                        </div>
                        <div>
                            <p class="font-weight-bold">{sgettext('$$RISK$$')}: {globalHazard.name}</p>
                            <p>{globalHazard.description}</p>
                        </div>
                    </div>
                    <div class="column name">
                        <div class="item-icon">
                            <img
                                src={globalControl.icon.url}
                                alt={sgettext('$$CONTROL_ICON$$')} />
                        </div>
                        <div>
                            <p class="font-weight-bold">{sgettext('$$CONTROL$$')}: {globalHazard.name}</p>
                            <p>{globalControl.description}</p>
                        </div>
                    </div>
                    <div class="column">
                        <Button type="link" name="{sgettext('$$EDIT_MATERIAL_RISK$$')}" on:click={openModal} />
                    </div>
                </div>
            {:else}
                <p class="mb-0">
                    <Button type="link pl-0" name="{sgettext('$$ADD_MATERIAL_RISK$$')}" on:click={openModal}/>
                </p>
            {/if}
        </div>


    <Modal {id} {title}>
        <div slot="body">
            <p>{sgettext('$$SAFETY_ELEMENT_PROVIDE_INSTRUCTIONS_SET$$')}</p>
            <p>{sgettext('$$PLEASE_SELECT_MATERIAL_RISK_ELEMENT$$')}</p>
            {#if loadingHazardOptions}
                <i class="fa fa-spinner fa-spin"></i>
            {:else}
                <div class="form-group">
                    <label for="mechanism" title="">{sgettext('$$MECHANISM_2$$')}</label>
                    <MegaSelect
                        id='mechanism'
                        skipWindowEventId='materialRiskEditorModal'
                        options={options}
                        headers={[
                            sgettext('$$HAZARDOUS_ENERGY$$'),
                            sgettext('$$WHAT_1$$'),
                            sgettext('$$SELECT_MECHANISM_1$$'),
                        ]}
                        placeholder={sgettext('$$SEARCHSELECT_MECHANISM_$$')}
                        noOptionsMessage={sgettext('$$RESULTS_FOUND$$')}
                        isListGrouped={true}
                        showImages={true}
                        listPlacement={mechanismListPlacement}
                        listAbsoluteParentId={mechanismListAbsoluteParentId}
                        listAbsoluteScrollParentSelector={mechanismListAbsoluteScrollParentSelector}
                        itemValueKey='id'
                        itemLabelKey='name'
                        groupLabelKey='name'
                        groupDescriptionKey='description'
                        getGroupImageUrl={group => group.icon ? group.icon.url : ''}
                        groupItemsKey='mechanism_options'
                        selectedItem={mechanism}
                        bind:groupOfSelectedItem={energyType}
                        onSelectItem={handleSelectMechanism}
                        onClear={handleClearMechanism}
                        clearable={true}
                        isWaiting={saving}
                    />
                </div>
                <div class="form-group">
                    <ComboSelect
                        id="global_hazard"
                        label={sgettext("$$RISK_1$$")}
                        options={hazardOptions}
                        bind:value={globalHazardId}
                        on:changed={handleHazardChanged}
                        searchable={true}
                        {schema}
                    />
                </div>
                <div class="form-group">
                    <ComboSelect
                        id="global_control"
                        label={sgettext("$$CONTROL_1$$")}
                        options={controlOptions}
                        bind:value={globalControlId}
                        searchable={true}
                        {schema}
                    />
                </div>
            {/if}
        </div>

        <div slot="footer">
        <Button name="{sgettext('$$SELECT$$')}"
                    disabled={globalHazardId == null || globalControlId == null}
                    on:click={finishSelection}
                     />
        </div>
    </Modal>
{:else}
    <div>
        <h3 class="text-primary">{sgettext('$$MATERIAL_RISK$$')}</h3>
        {#if previouslySelectedMaterialRiskIsNoLongerValid && !changes}
            <Messages type="warning" text={`${sgettext('$$CURRENT_MATERIAL_RISK_CONFIGURATION_NOT$$')}`} />
        {/if}
        <p>{sgettext('$$PLEASE_SELECT_MATERIAL_RISK_SAFETY$$')}</p>
    </div>

    <div class="details-row" id='scrollHazardControl' style="{ (previouslySelectedMaterialRiskIsNoLongerValid && !changes) ? 'border:1px solid red' : 'border:none'}">
        {#if loadingHazardOptions}
            <div>
                <i class="fas fa-spinner fa-spin"></i>
            </div>
        {:else}
            <div class="form-group">
                <label for="mechanism" title="">{sgettext('$$MECHANISM_2$$')}</label>
                {#if previouslySelectedMaterialRiskIsNoLongerValid && !changes}
                    <i class="fa fa-exclamation-circle text-danger" style="display: flex;margin-top: 14px;margin-left: -17px;margin-bottom: -30px"/>
                {/if}
                <MegaSelect
                    id='mechanism'
                    options={options}
                    headers={[
                        sgettext('$$HAZARDOUS_ENERGY$$'),
                        sgettext('$$WHAT_1$$'),
                        sgettext('$$SELECT_MECHANISM_1$$'),
                    ]}
                    placeholder={sgettext('$$SEARCHSELECT_MECHANISM_$$')}
                    noOptionsMessage={sgettext('$$RESULTS_FOUND$$')}
                    isListGrouped={true}
                    showImages={true}
                    listPlacement={mechanismListPlacement}
                    listAbsoluteParentId={mechanismListAbsoluteParentId}
                    listAbsoluteScrollParentSelector={mechanismListAbsoluteScrollParentSelector}
                    itemValueKey='id'
                    itemLabelKey='name'
                    groupLabelKey='name'
                    groupDescriptionKey='description'
                    getGroupImageUrl={group => group.icon ? group.icon.url : ''}
                    groupItemsKey='mechanism_options'
                    selectedItem={mechanism}
                    bind:groupOfSelectedItem={energyType}
                    onSelectItem={handleSelectMechanism}
                    onClear={handleClearMechanism}
                    clearable={true}
                    isWaiting={saving}
                />
            </div>
            <div class="form-group">
                <ComboSelect
                    id="global_hazard"
                    label={sgettext("$$RISK_1$$")}
                    options={hazardOptions}
                    bind:value={globalHazardId}
                    on:changed={handleHazardChanged}
                    searchable={true}
                    {schema}
                    riskValid={false}
                    hazardText="globalHazard"
                />
            </div>
            <div class="form-group">
                <ComboSelect
                    id="global_control"
                    label={sgettext("$$CONTROL_1$$")}
                    options={controlOptions}
                    bind:value={globalControlId}
                    searchable={true}
                    {schema}
                />
            </div>
        {/if}
    </div>
    <div class="mt-3 text-right">
        {#if changes}
            <Button type="secondary" name={sgettext('$$CANCEL$$')} on:click={revertChanges} />
            <Button type="primary" disabled={globalHazardId == null || globalControlId == null} name={sgettext('$$SAVE_CHANGES$$')} on:click={saveChanges} />
        {:else if saving}
            <i class="fas fa-spinner fa-spin"></i>
        {/if}

        {#if saveSuccess}
            <i class="fas fa-check text-success"></i>
        {/if}
    </div>
{/if}
